/**
 * Blog content registry.
 *
 * Drop a Markdown file in src/content/blog/ and it shows up automatically:
 *   - <slug>.md      → English (default / fallback)
 *   - <slug>.pt.md   → Brazilian Portuguese
 *
 * Frontmatter: title, summary, date (YYYY-MM-DD), tags: [a, b, c]
 */

export type Frontmatter = {
  title?: string;
  summary?: string;
  date?: string;
  tags?: string[];
};

export type Post = {
  slug: string;
  lang: "en" | "pt";
  frontmatter: Frontmatter;
  content: string;
};

function parseFrontmatter(raw: string): { data: Frontmatter; content: string } {
  if (!raw.startsWith("---")) return { data: {}, content: raw };
  const end = raw.indexOf("\n---", 3);
  if (end === -1) return { data: {}, content: raw };

  const header = raw.slice(3, end).trim();
  const rest = raw.slice(end + 4);
  const data: Frontmatter = {};

  for (const line of header.split(/\r?\n/)) {
    const m = line.match(/^([A-Za-z0-9_-]+)\s*:\s*(.*)$/);
    if (!m) continue;
    const key = m[1].trim();
    let value = m[2].trim();

    if ((value.startsWith('"') && value.endsWith('"')) || (value.startsWith("'") && value.endsWith("'"))) {
      value = value.slice(1, -1);
    }

    if (value.startsWith("[") && value.endsWith("]")) {
      const inner = value.slice(1, -1).trim();
      (data as Record<string, string | string[]>)[key] = inner.length
        ? inner.split(",").map((s) => s.trim().replace(/^['"]|['"]$/g, "")).filter(Boolean)
        : [];
      continue;
    }

    (data as Record<string, string | string[]>)[key] = value;
  }

  return { data, content: rest.trimStart() };
}

const files = import.meta.glob("/src/content/blog/*.md", {
  query: "?raw",
  import: "default",
  eager: true,
}) as Record<string, string>;

const all: Post[] = Object.entries(files).map(([path, raw]) => {
  const file = (path.split("/").pop() || "").replace(/\.md$/i, "");
  let lang: "en" | "pt" = "en";
  let slug = file;
  if (file.endsWith(".pt")) {
    lang = "pt";
    slug = file.slice(0, -3);
  }
  const { data, content } = parseFrontmatter(raw);
  return { slug, lang, frontmatter: data, content };
});

const toLang = (language: string): "en" | "pt" => (language.startsWith("pt") ? "pt" : "en");

/** All posts for the given language (falls back to English), newest first. */
export function getPosts(language: string): Post[] {
  const lang = toLang(language);
  const slugs = [...new Set(all.map((p) => p.slug))];
  return slugs
    .map(
      (slug) =>
        all.find((p) => p.slug === slug && p.lang === lang) ||
        all.find((p) => p.slug === slug && p.lang === "en")
    )
    .filter((p): p is Post => Boolean(p))
    .sort((a, b) => (b.frontmatter.date || "").localeCompare(a.frontmatter.date || ""));
}

/** A single post by slug for the given language (falls back to English). */
export function getPost(slug: string, language: string): Post | null {
  const lang = toLang(language);
  const decoded = decodeURIComponent(slug);
  return (
    all.find((p) => p.slug === decoded && p.lang === lang) ||
    all.find((p) => p.slug === decoded && p.lang === "en") ||
    null
  );
}
